import {
  DateTime,
  DateTimeUnit,
  Duration,
  DurationObjectUnits,
  Interval,
  LocaleOptions,
} from "luxon"
import type { TFunction } from "react-i18next"
import { CalendarDurationType, Maybe } from "@oysterhr/schema"

export type LuxonCompatibleDateTypes = DateTime | Date | string

/**
 * @description
 * Formats the date in the following format: 29 Aug 2022, 03:22 PM
 */
export const fullDateTime = (date: LuxonCompatibleDateTypes): string => {
  return toLuxonDateTime(date).toLocaleString({
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  })
}

const LOCAL_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone
/**
 *
 * @param date the object obtained from parsing the backend  timestamp which is in the UTC zone
 * @returns a DateTime object that contains the  local timezone
 */
export const toLocalTimezone = (date: LuxonCompatibleDateTypes) => {
  return toLuxonDateTime(date).setZone(LOCAL_TIMEZONE)
}

/**
 * @deprecated Please use Luxon
 * @see https://moment.github.io/luxon/#/formatting?id=tolocalestring-strings-for-humans
 *
 * Returns formated string for a given date according to rules i options param:
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
 * @param {string | Date} date like "2020-09-23" or js date object
 * @param {Intl.DateTimeFormatOptions} options like { month: "long", year: "numeric" }
 */
export const formattedDateString = (
  date: string | Date,
  options: Intl.DateTimeFormatOptions,
): string => {
  const parsedDate = typeof date === "string" ? DateTime.fromISO(date) : DateTime.fromJSDate(date)
  return parsedDate.toJSDate().toLocaleDateString("en-GB", options)
}

// Takes an iso8601 duration string, or a luxon duration object and returns in format 1h 2min
export const formatDurationToHMin = (
  duration: string | Duration,
  hoursOnly = false,
  showZeroMins = true,
): string => {
  let parsedDuration =
    typeof duration === "string" ? Duration.fromISO(duration).shiftTo("hours", "minutes") : duration
  let negative
  if (parsedDuration.valueOf() < 0) {
    parsedDuration = parsedDuration.negate()
    negative = true
  }
  return `${negative ? "-" : ""}${parsedDuration.hours}h${
    hoursOnly
      ? ""
      : parsedDuration.minutes > 0 || showZeroMins
        ? ` ${parsedDuration.minutes}min`
        : ""
  }`
}

export const formatDurationToDays = (durationString = "PT0S", t: TFunction) => {
  const duration = Duration.fromISO(durationString)
  // round to the nearest half day, e.g. 1 day, 1.5 days
  const days = Math.round((duration.as("hours") / 8) * 2) / 2
  return `${days} ${t("days", { count: days })}`
}

export const timeAgo = (date: string) => {
  return DateTime.fromISO(date).toRelative()
}

// Accepts an ISO8601 string or a Luxon DateTime object and provides a formatted date or a string representation of this time in relation to the present
// For instance, it could say "1 hour ago" if the difference between the current time and the provided time is less then a day
// Otherwise, it returns the date in a format like "4 Sept 2022"
export const timeAgoOrDate = (date: string | DateTime) => {
  const parsedTime = typeof date === "string" ? DateTime.fromISO(date) : date

  if (Math.abs(parsedTime.diffNow("days").days) < 1) {
    return parsedTime.toRelative()
  } else {
    return parsedTime.toLocaleString(DateTime.DATE_MED)
  }
}

// Takes an iso8601 duration string and returns in format 1h 30m
export function formatDurationToHoursMinutes(durationString = "PT0S"): string {
  const duration = Duration.fromISO(durationString).shiftTo("hours", "minutes")
  const { hours, minutes } = duration.toObject()
  // DateTime presets do not include duration formats.
  // eslint-disable-next-line oyster/frog-forbidden-date
  if (!hours && !minutes) return duration.toFormat("h'h'")

  const format = hours && minutes ? "h'h' mm'm'" : hours ? "h'h'" : "mm'm'"

  // DateTime presets do not include duration formats.
  // eslint-disable-next-line oyster/frog-forbidden-date
  return duration.toFormat(format)
}

// Takes an iso8601 duration string, and returns in format 00:00
export function formatDurationToColonNotation(durationString = "PT0S"): string {
  // we need the format hh:mm for the duration which isn't available on toHuman()
  // eslint-disable-next-line oyster/frog-forbidden-date
  return Duration.fromISO(durationString).toFormat("hh:mm")
}

// Converts any Date into YYYY-MM-DD string, ignores timezones
export const convertDateToIsoString = (date: Date): string => {
  const year = date.getFullYear()
  const month = (date.getMonth() + 1).toString().padStart(2, "0") //months are zero based
  const day = date.getDate().toString().padStart(2, "0")
  return `${year}-${month}-${day}`
}

export type MonthNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12

type DateConvertable = Date | string | number | null | undefined

/**
 * Creates a new date of the day for current timezone but in UTC
 * Why?
 * To create a date fit for storing in the db based on a date picker
 * which creates a date in local time
 */
export function sameDateInUTC(inputDate?: DateConvertable): Date {
  if (typeof inputDate === "string") {
    return DateTime.fromISO(inputDate).toJSDate()
  }
  const date =
    !inputDate || typeof inputDate === "number" ? DateTime.now() : DateTime.fromJSDate(inputDate)
  return date.toJSDate()
}

export const getShortMonthsOptions = (t: TFunction) => {
  return [
    { label: t("months.short.january"), value: "01" },
    { label: t("months.short.february"), value: "02" },
    { label: t("months.short.march"), value: "03" },
    { label: t("months.short.april"), value: "04" },
    { label: t("months.short.may"), value: "05" },
    { label: t("months.short.june"), value: "06" },
    { label: t("months.short.july"), value: "07" },
    { label: t("months.short.august"), value: "08" },
    { label: t("months.short.september"), value: "09" },
    { label: t("months.short.october"), value: "10" },
    { label: t("months.short.november"), value: "11" },
    { label: t("months.short.december"), value: "12" },
  ]
}

/**
 * Check for a specific date, if it's a business day
 */
export function isBusinessDay(date: Date): boolean {
  const day = DateTime.fromJSDate(date).weekday
  const SATURDAY = 6
  const SUNDAY = 7

  return day !== SATURDAY && day !== SUNDAY
}

/**
 *
 * @param {Date} date - The date you want to start calculating from
 * @param {boolean} skipCurrentDay - Skip the current day
 * @returns Date
 */
export function getNextBusinessDay(date: Date, skipCurrentDay = false): Date {
  // There are use cases where we want to skip the current day on the calculations, so if that's the case, we just start in the next day
  let nextBusinessDay = DateTime.fromJSDate(date).plus({ days: skipCurrentDay ? 1 : 0 })

  while (!isBusinessDay(nextBusinessDay.toJSDate())) {
    nextBusinessDay = nextBusinessDay.plus({ days: 1 })
  }

  return nextBusinessDay.toJSDate()
}

export function addCalendarMonths(date: Date, numberOfMonths: number) {
  return DateTime.fromJSDate(date).plus({ months: numberOfMonths }).toJSDate()
}

export const DURATION_KEYS = ["months", "weeks", "days", "hours"] as const

export function formatDuration(duration: { [key: string]: number }, t: TFunction) {
  const result = DURATION_KEYS.map(unit =>
    duration[unit] !== undefined ? `${duration[unit]} ${t(unit, { count: duration[unit] })}` : "",
  )
  return result.join(" ")
}

export const HALF_DAYS = ["morning", "afternoon"]

export function formatDateEndpoint(
  date: Date,
  timePeriod: string | null | undefined,
  t: TFunction,
) {
  const formattedDate = toLuxonDateTime(date).toLocaleString(DateTime.DATE_MED)
  const formattedTimePeriod = t(`timePeriods.${timePeriod}`)

  return timePeriod && HALF_DAYS.includes(timePeriod)
    ? `${formattedDate} (${formattedTimePeriod})`
    : formattedDate
}

/**
 * Returns working day(weekday) count in given interval
 * @returns number of working day(weekday) count in the passed interval
 */
export function getWorkingDaysCount(
  startDate: DateTime | Date | string,
  endDate: DateTime | Date | string,
) {
  const startDateLuxon = toLuxonDateTime(startDate)
  const endDateLuxon = toLuxonDateTime(endDate)
  const weekends = [6, 7] // Saturday, Sunday
  let workingDaysCount = 0

  let currentDateLuxon = startDateLuxon
  while (currentDateLuxon <= endDateLuxon) {
    if (!weekends.includes(currentDateLuxon.weekday)) {
      workingDaysCount++
    }
    currentDateLuxon = currentDateLuxon.plus({ days: 1 })
  }

  return workingDaysCount
}

/**
 * Returns true if last day of month, false otherwise
 */
export function isLastDayOfMonth(date: DateTime | Date | string) {
  let test = toLuxonDateTime(date)
  test = test.plus({ days: 1 })
  return test.day === 1
}

/**
 * The last case assumes an ISO string - there are other formats but it's unlikely we'll be using them
 * https://moment.github.io/luxon/#/parsing?id=parsing-technical-formats
 * @param date formats convertible by Luxon
 */
export function toLuxonDateTime(date: DateTime | Date | string) {
  return DateTime.isDateTime(date)
    ? date
    : date instanceof Date
      ? DateTime.fromJSDate(date)
      : DateTime.fromISO(date)
}

export function getDifferenceInDays(
  dateA: DateConvertable = DateTime.now().toJSDate(),
  dateB: DateConvertable = DateTime.now().toJSDate(),
): number {
  const timeA =
    !!dateA && typeof dateA === "object" ? DateTime.fromJSDate(dateA) : DateTime.fromISO(`${dateA}`)
  const timeB =
    !!dateB && typeof dateB === "object" ? DateTime.fromJSDate(dateB) : DateTime.fromISO(`${dateB}`)

  const diffInDays = timeB.diff(timeA, "days").days

  return Math.abs(Math.round(diffInDays))
}
export const UNIT_PROP_MAP: Record<keyof DurationObjectUnits, DateTimeUnit> = {
  milliseconds: "millisecond",
  months: "month",
  years: "year",
  days: "day",
  hours: "hour",
  minutes: "minute",
  seconds: "second",
  weeks: "week",
  quarters: "quarter",
} as const
/**
 *  Checks if the date in the first argument is chronologically before or is the same with the
 *  date in the second argument. The third argument is used for the time unit that is compared.
 *  @example isSameOrBefore('2022-01-25', '2022-02-01' , 'months') //  true
 *  @example isSameOrBefore('2022-01-25', '2022-02-01' , 'quarters') //  true
 *  @example isSameOrBefore('2023-01-25', '2022-01-25' , 'quarters') //  false
 */
export function isSameOrBefore(
  date: LuxonCompatibleDateTypes,
  comparedTo: LuxonCompatibleDateTypes = DateTime.now(),
  unit: keyof DurationObjectUnits = "milliseconds",
): boolean {
  const luxDate = toLuxonDateTime(date)
  const luxComparedTo = toLuxonDateTime(comparedTo)
  return luxDate.startOf(UNIT_PROP_MAP[unit]) <= luxComparedTo.startOf(UNIT_PROP_MAP[unit])
}
/**
 *  Checks if the date in the first argument is chronologically after or is the same with the
 *  date in the second argument. The third argument is used for the time unit that is compared.
 *  @example isSameOrAfter('2022-01-25', '2022-02-01' , 'months') //  false
 *  @example isSameOrAfter('2022-01-25', '2022-02-01' , 'quarters') //  false
 *  @example isSameOrAfter('2023-01-25', '2022-01-25' , 'quarters') //  true
 */
export function isSameOrAfter(
  date: LuxonCompatibleDateTypes,
  comparedTo: LuxonCompatibleDateTypes = DateTime.now(),
  unit: keyof DurationObjectUnits = "milliseconds",
): boolean {
  const luxDate = toLuxonDateTime(date)
  const luxComparedTo = toLuxonDateTime(comparedTo)
  return luxDate.startOf(UNIT_PROP_MAP[unit]) >= luxComparedTo.startOf(UNIT_PROP_MAP[unit])
}

/**
 *  Checks if the date in the first argument is chronologically after the date in the second argument.
 *  The third argument is used for the time unit that is compared.
 *  @example isAfter('2022-01-25', '2022-02-01' , 'months') //  false
 *  @example isAfter('2022-02-25', '2022-01-01' , 'quarters') //  false
 *  @example isAfter('2023-01-25', '2022-01-25' , 'quarters') //  true
 */
export function isAfter(
  date: LuxonCompatibleDateTypes,
  comparedTo: LuxonCompatibleDateTypes = DateTime.now(),
  unit: keyof DurationObjectUnits = "milliseconds",
) {
  return !isSameOrBefore(date, comparedTo, unit)
}

/**
 *  Checks if the date in the first argument is chronologically after the date in the second argument.
 *  The third argument is used for the time unit that is compared.
 *  @example isBefore('2022-01-25', '2022-02-01' , 'months') //  true
 *  @example isBefore('2022-02-25', '2022-01-01' , 'quarters') //  false
 *  @example isBefore('2023-01-25', '2022-01-25' , 'quarters') //  false
 */
export function isBefore(
  date: LuxonCompatibleDateTypes,
  comparedTo: LuxonCompatibleDateTypes = DateTime.now(),
  unit: keyof DurationObjectUnits = "milliseconds",
) {
  return !isSameOrAfter(date, comparedTo, unit)
}

export type CalendarDurationLike = {
  unit: `${CalendarDurationType}`
  value?: Maybe<number> | undefined
}

export const formatDate = (date: string, format?: string, options?: LocaleOptions) => {
  const formatString = format || "cccc, LLLL d, yyyy"

  // I'll fix this in a separate PR, as it need to be adjusted across the app
  // eslint-disable-next-line oyster/frog-forbidden-date
  return DateTime.fromISO(date).toFormat(formatString, options)
}

/**
 * Rounds decimal part of the day to two digits behind comma. Method accepts days decimal
 * part or hours as days number, but in that case calculateDays boolean should be true.
 * By default it's considered to be 8 hours per working day.
 * @param {number} amount - Decimal part of the day or hours
 * @param {boolean} calculateDays - In case hours is provided, this should be true
 * @param {number} hoursPerDay - Hours per working day
 * @returns number
 */
export function roundDays(amount: number, calculateDays = false, hoursPerDay = 8): number {
  if (calculateDays) {
    amount = amount / hoursPerDay
  }

  return Math.round(amount * 100) / 100
}

/**
 * Returns an array of DateTimes for each Saturday and Sunday within a range of a start date and end date
 * @param {DateTime} startDate - Beginning of range
 * @param {DateTime} endDate - End of range
 * @returns (DateTime | null)[]
 */
export const eachWeekendOfInterval = (
  startDate: DateTime,
  endDate: DateTime,
): (DateTime | null)[] =>
  Interval.fromDateTimes(startDate, endDate)
    .splitBy({ day: 1 })
    .map(d => (d.start.weekday === 6 ? d.start : d.start.weekday === 7 ? d.start : null))
    .filter(date => !!date)

/**
 * Creates a range of months from a start month and an end month.
 * Inclusive of the start and end month.
 *
 * By default, this function returns a list of months from January till December.
 * The `format` field could be optionally provided. The  default `format` is `luxon`'s `MonthNumbers`.
 *
 * However, to get a custom list, the `startMonth` and `endMonth` fields can be optionally provided.
 * @example getRangeOfMonths() //  [{name: "Jan, number: 1}, ..., {name: "Dec", number: 12}]
 * @example getRangeOfMonths({ format: "long" }) //  [{name: "January", number: 1 }, ..., {name: "December", number: 12}]
 * @example getRangeOfMonths({ startMonth: 3, endMonth: 12, format: "short" }) //  [{name: "Mar", number: 3}, ..., {name: "Nov", number: 11}]
 */
export const getRangeOfMonths = ({
  startMonth,
  endMonth,
  format,
}: { startMonth?: number; endMonth?: number; format?: "short" | "long" } = {}): {
  name: string
  number: MonthNumber
}[] => {
  const startDateTime = startMonth
    ? DateTime.fromObject({ month: startMonth })
    : DateTime.now().startOf("year")

  const endDateTime = endMonth
    ? // set to date to last day of month so the returned Interval.fromDateTimes is inclusive of this month.
      DateTime.fromObject({ month: endMonth }).endOf("month")
    : DateTime.now().endOf("year")

  const getMonthFormat = (dt: Interval) => {
    return format === "long" ? dt.start.monthLong : dt.start.monthShort
  }

  return Interval.fromDateTimes(startDateTime, endDateTime)
    .splitBy({ month: 1 })
    .map(dt => ({ name: getMonthFormat(dt), number: dt.start.month }))
}

/**
 * Creates a range of years from a start year and an end year.
 * Inclusive of the start but not the end.
 *
 * By default, this function returns a list of years from 1970 till the current year.
 *
 * @param {number} [startYear=1970] - Start year of the range. The default value is 1970
 * @param {number} [endYear] - End year of the range.
 * @return {Array}  List of years
 *
 * @example getRangeOfYears() //  [1970, ..., {currentYear}]
 * @example getRangeOfYears(2000) //  [2000, ..., {currentYear}]
 * @example getRangeOfYears(2020, 2023) //  [2020, 2021, 2022]
 */
export const getRangeOfYears = (startYear?: number, endYear?: number): Array<number> => {
  // 1970 is used being the beginning Unix epoch time
  const startDateTime = DateTime.fromObject({ year: startYear || 1970 })
  const endDateTime = endYear ? DateTime.fromObject({ year: endYear }) : DateTime.now()

  return Interval.fromDateTimes(startDateTime, endDateTime)
    .splitBy({ year: 1 })
    .map(dt => dt.start.year)
}
/**
 * Extracts the weekday from a date.
 *
 * @param {string} date in ISO format
 * @returns The localized matching weekday, e.g. Monday
 */
export const formatDateWeekday = (date: string) => {
  return `${DateTime.fromISO(date).toLocaleString({
    weekday: "long",
  })}`
}

/**
 *
 * @param {string} date in ISO format
 * @returns The localized month and day, e.g. November 28
 */
export const formatDateDayMonth = (date: string) => {
  return `${DateTime.fromISO(date).monthLong} ${DateTime.fromISO(date).day}`
}

/**
 *
 * @param {Duration | string} duration, e.g "PT0S"
 * @returns true if the duration is empty (0 seconds), false otherwise
 */
export const isEmptyDuration = (duration?: Duration | string | null) => {
  const parsedDuration = typeof duration === "string" ? Duration.fromISO(duration) : duration
  if (!parsedDuration || !parsedDuration.isValid) return true
  return parsedDuration.toMillis() === 0
}
